import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Fade,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Header from './Header';
import OnboardingForm from './onboarding/OnboardingForm';
import OnboardingSuccess from './onboarding/OnboardingSuccess';
import ProgressIndicator from './onboarding/ProgressIndicator';
import { getCSRFToken } from '../utils/csrf';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import emailjs from 'emailjs-com';

// API Base URL - uses environment variable or defaults based on current host
// For production: https://super.nasradius.com
// For local dev: http://localhost:8000
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
    ? "http://localhost:8000" 
    : "https://super.nasradius.com");

const OnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('form'); // 'form' | 'processing' | 'success' | 'error'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    username: '',
    subdomain: '',
    provider: 'NasRadiusPay',
    mode: 'Till',
    mpesa_consumer_key: '',
    mpesa_consumer_secret: '',
    mpesa_shortcode: '',
    mpesa_till_number: '',
    mpesa_passkey: '',
    kopokopo_client_id: '',
    kopokopo_client_secret: '',
    kopokopo_stk_till_number: '',
    channel: 'BuyGoods',
    shortcode: '',
    paybill_number: '',
    account_number: '',
    bank_name: '',
  });
  
  // SSE progress tracking state
  const [processingSteps, setProcessingSteps] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [currentStepId, setCurrentStepId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [failedStepId, setFailedStepId] = useState(null);
  const eventSourceRef = useRef(null);
  
  // Subdomain availability checking
  const [subdomainStatus, setSubdomainStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const subdomainCheckTimeoutRef = useRef(null);
  
  // Username availability checking
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const usernameCheckTimeoutRef = useRef(null);

  // Prefill form from URL parameters
  useEffect(() => {
    const name = searchParams.get('name');
    const email = searchParams.get('email');
    const phone = searchParams.get('phone');
    const username = searchParams.get('username');
    const subdomain = searchParams.get('subdomain');

    if (name || email || phone || username || subdomain) {
      const decodedPhone = decodeURIComponent(phone || '');
      setFormData(prev => ({
        ...prev,
        name: name ? decodeURIComponent(name) : prev.name,
        email: email ? decodeURIComponent(email) : prev.email,
        phone: phone ? (decodedPhone ? formatPhoneNumber(decodedPhone) : '') : prev.phone,
        username: username ? decodeURIComponent(username).toLowerCase().replace(/[^a-z0-9_]/g, '') : prev.username,
        subdomain: subdomain ? decodeURIComponent(subdomain).replace(/^https?:\/\//i, '').replace(/\.nasradius\.com.*$/i, '').trim() : prev.subdomain,
      }));
    }
  }, [searchParams]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Format phone number before validation
    const formattedPhone = formatPhoneNumber(formData.phone.trim());
    
    // Update form data if phone was formatted
    if (formattedPhone !== formData.phone.trim()) {
      setFormData((prev) => ({ ...prev, phone: formattedPhone }));
    }

    // Create updated form data for validation
    const dataToValidate = formattedPhone !== formData.phone.trim()
      ? { ...formData, phone: formattedPhone }
      : formData;

    // Validate with formatted phone
    const newErrors = {};
    if (!dataToValidate.name || dataToValidate.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!dataToValidate.email || !emailPattern.test(dataToValidate.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    const cleanedPhone = formattedPhone.replace(/[\s-]/g, '');
    const phonePattern = /^\+?[1-9]\d{1,14}$/;
    if (!formattedPhone || !phonePattern.test(cleanedPhone)) {
      newErrors.phone = 'Please enter a valid phone number (e.g., +254712345678)';
    }
    
    // Username validation
    const usernamePattern = /^[a-z0-9_]{3,30}$/;
    const usernameValue = dataToValidate.username.toLowerCase();
    
    if (!dataToValidate.username) {
      newErrors.username = 'Username is required';
    } else if (!usernamePattern.test(usernameValue)) {
      newErrors.username = 'Username must be 3-30 characters (lowercase letters, numbers, underscores only)';
    } else if (usernameStatus === 'taken') {
      newErrors.username = 'This username is already taken. Please choose another.';
    } else if (usernameStatus === 'checking') {
      newErrors.username = 'Please wait, checking username availability...';
    } else if (usernameStatus === null && usernameValue.length >= 3) {
      // Haven't checked yet - need to check before submitting
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
      
      setErrors(prev => ({ ...prev, username: 'Checking username availability...' }));
      setUsernameStatus('checking');
      
      // Run immediate check
      fetch(`${API_BASE_URL}/api/onboarding/check-username?username=${encodeURIComponent(usernameValue)}`)
        .then(res => res.json())
        .then(data => {
          if (data.available) {
            setUsernameStatus('available');
            setErrors({});  // Clear all errors
            // Username is available - submit the form directly
            // (Don't call handleSubmit recursively - React state closure issue)
            submitForm(formattedPhone);
          } else {
            setUsernameStatus('taken');
            setErrors({ username: 'This username is already taken. Please choose another.' });
          }
        })
        .catch(err => {
          console.error('Error checking username:', err);
          setUsernameStatus(null);
          setErrors({});  // Clear errors
          // Proceed anyway on error - backend will validate
          submitForm(formattedPhone);
        });
      return;
    }
    
    const subdomainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
    const subdomainValue = dataToValidate.subdomain.toLowerCase();
    
    if (!dataToValidate.subdomain) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!subdomainPattern.test(subdomainValue)) {
      newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    } else if (dataToValidate.subdomain.length > 63) {
      newErrors.subdomain = 'Subdomain must be 63 characters or less';
    } else if (dataToValidate.subdomain.startsWith('-') || dataToValidate.subdomain.endsWith('-')) {
      newErrors.subdomain = 'Subdomain cannot start or end with a hyphen';
    } else if (subdomainStatus === 'taken') {
      // Subdomain is already confirmed as taken
      newErrors.subdomain = 'This subdomain is already taken. Please choose another.';
    } else if (subdomainStatus === 'checking') {
      // Still checking - show waiting message
      newErrors.subdomain = 'Please wait, checking subdomain availability...';
    } else if (subdomainStatus === null && subdomainValue.length >= 2) {
      // Haven't checked yet - need to check before submitting
      // Cancel any pending debounced check
      if (subdomainCheckTimeoutRef.current) {
        clearTimeout(subdomainCheckTimeoutRef.current);
      }
      
      // Show checking message and run immediate check
      setErrors({ subdomain: 'Checking subdomain availability...' });
      setSubdomainStatus('checking');
      
      // Run immediate check - it will set the status and error if taken
      fetch(`${API_BASE_URL}/api/onboarding/check-subdomain?subdomain=${encodeURIComponent(subdomainValue)}`)
        .then(res => res.json())
        .then(data => {
          if (data.available) {
            setSubdomainStatus('available');
            setErrors({});
            // Auto-submit after successful check
            submitForm(formattedPhone);
          } else {
            setSubdomainStatus('taken');
            setErrors({ subdomain: 'This subdomain is already taken. Please choose another.' });
          }
        })
        .catch(err => {
          console.error('Error checking subdomain:', err);
          setSubdomainStatus(null);
          setErrors({});
          // Proceed anyway on error - backend will validate
          submitForm(formattedPhone);
        });
      return;
    }

    // Validate provider-specific payment settings
    if (dataToValidate.provider === 'M-PESA') {
      if (!dataToValidate.mpesa_consumer_key) newErrors.mpesa_consumer_key = 'M-PESA Consumer Key is required';
      if (!dataToValidate.mpesa_consumer_secret) newErrors.mpesa_consumer_secret = 'M-PESA Consumer Secret is required';
      if (!dataToValidate.mpesa_shortcode) newErrors.mpesa_shortcode = 'M-PESA Shortcode is required';
      if (!dataToValidate.mpesa_till_number) newErrors.mpesa_till_number = 'M-PESA Till Number is required';
      if (!dataToValidate.mpesa_passkey) newErrors.mpesa_passkey = 'M-PESA Passkey is required';
    } else if (dataToValidate.provider === 'Kopokopo') {
      if (!dataToValidate.kopokopo_client_id) newErrors.kopokopo_client_id = 'Kopokopo Client ID is required';
      if (!dataToValidate.kopokopo_client_secret) newErrors.kopokopo_client_secret = 'Kopokopo Client Secret is required';
      if (!dataToValidate.kopokopo_stk_till_number) newErrors.kopokopo_stk_till_number = 'Kopokopo STK Till Number is required';
    } else if (dataToValidate.provider === 'NasRadiusPay') {
      if (!dataToValidate.channel) newErrors.channel = 'Payment channel is required';
      if (dataToValidate.channel === 'BuyGoods' && !dataToValidate.shortcode) newErrors.shortcode = 'Till number (shortcode) is required for BuyGoods';
      if (dataToValidate.channel === 'PayBill' && !dataToValidate.paybill_number) newErrors.paybill_number = 'Paybill number is required';
      if (dataToValidate.channel === 'PayBill' && !dataToValidate.account_number) newErrors.account_number = 'Account number is required';
      if (dataToValidate.channel === 'BankPayBill' && !dataToValidate.bank_name) newErrors.bank_name = 'Bank name is required';
      if (dataToValidate.channel === 'BankPayBill' && !dataToValidate.paybill_number) newErrors.paybill_number = 'Bank paybill number is required';
      if (dataToValidate.channel === 'BankPayBill' && !dataToValidate.account_number) newErrors.account_number = 'Account number is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    submitForm(formattedPhone);
  };

  // Cleanup SSE connection and check timeouts on unmount
  useEffect(() => {
    const eventSource = eventSourceRef.current;
    const subdomainTimeoutRef = subdomainCheckTimeoutRef.current;
    const usernameTimeoutRef = usernameCheckTimeoutRef.current;
    return () => {
      if (eventSource) {
        eventSource.close();
      }
      if (subdomainTimeoutRef) {
        clearTimeout(subdomainTimeoutRef);
      }
      if (usernameTimeoutRef) {
        clearTimeout(usernameTimeoutRef);
      }
    };
  }, []);

  // Send confirmation email when onboarding succeeds
  useEffect(() => {
    if (successData && step === 'success') {
      sendConfirmationEmail(successData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successData, step]);

  const submitForm = useCallback(async (phoneNumber) => {
    setIsLoading(true);
    setStep('processing');
    setErrorMessage('');
    setProcessingSteps([]);
    setCurrentStepId(null);
    setFailedStepId(null);

    // Close any existing SSE connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Use fetch with streaming for SSE (since EventSource only supports GET)
      const response = await fetch(`${API_BASE_URL}/api/onboarding/start-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: phoneNumber,
          username: formData.username.trim().toLowerCase(),
          subdomain: formData.subdomain.trim().toLowerCase(),
          payment_provider: formData.provider,
          payment_data: {
            provider: formData.provider,
            channel: formData.channel,
            shortcode: formData.shortcode,
            paybill_number: formData.paybill_number,
            account_number: formData.account_number,
            bank_name: formData.bank_name,
            mpesa_consumer_key: formData.mpesa_consumer_key,
            mpesa_consumer_secret: formData.mpesa_consumer_secret,
            mpesa_shortcode: formData.mpesa_shortcode,
            mpesa_till_number: formData.mpesa_till_number,
            mpesa_passkey: formData.mpesa_passkey,
            mode: formData.mode,
            kopokopo_client_id: formData.kopokopo_client_id,
            kopokopo_client_secret: formData.kopokopo_client_secret,
            kopokopo_stk_till_number: formData.kopokopo_stk_till_number,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setErrorMessage(errorData.message || 'Failed to start onboarding.');
        setStep('error');
        setIsLoading(false);
        return;
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        let eventType = '';
        let eventData = '';
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.substring(6).trim();
          } else if (line === '' && eventType && eventData) {
            // Complete event received
            try {
              const data = JSON.parse(eventData);
              handleSSEEvent(eventType, data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
            eventType = '';
            eventData = '';
          }
        }
      }
    } catch (error) {
      console.error('Onboarding SSE error:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
      setStep('error');
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleSSEEvent = useCallback((eventType, data) => {
    switch (eventType) {
      case 'progress':
        // Update steps list if provided
        if (data.all_steps && processingSteps.length === 0) {
          setProcessingSteps(data.all_steps.map(s => ({
            ...s,
            status: 'pending'
          })));
        }
        
        // Update the specific step status
        setProcessingSteps(prev => {
          const updated = [...prev];
          const stepIndex = updated.findIndex(s => s.id === data.step_id);
          
          // If a new step is starting (in_progress), mark all previous steps as completed
          if (data.status === 'in_progress' && stepIndex > 0) {
            for (let i = 0; i < stepIndex; i++) {
              if (updated[i].status !== 'failed') {
                updated[i] = {
                  ...updated[i],
                  status: 'completed',
                };
              }
            }
          }
          
          if (stepIndex >= 0) {
            updated[stepIndex] = {
              ...updated[stepIndex],
              status: data.status,
              message: data.message,
              error: data.error,
            };
          }
          return updated;
        });
        
        if (data.status === 'in_progress') {
          setCurrentStepId(data.step_id);
        } else if (data.status === 'failed') {
          setFailedStepId(data.step_id);
        }
        break;
        
      case 'success':
        // Mark all steps as completed on success
        setProcessingSteps(prev => prev.map(s => ({
          ...s,
          status: s.status !== 'failed' ? 'completed' : s.status
        })));
        setSuccessData(data);
        setStep('success');
        setIsLoading(false);
        // Email will be sent via useEffect watching successData
        break;
        
      case 'error':
        setErrorMessage(data.message || 'Onboarding failed. Please try again.');
        setFailedStepId(data.step);
        setStep('error');
        setIsLoading(false);
        break;
        
      default:
        console.warn('Unknown SSE event type:', eventType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingSteps.length]);

  // Check subdomain availability with debounce
  const checkSubdomainAvailability = useCallback(async (subdomain) => {
    if (!subdomain || subdomain.length < 2) {
      setSubdomainStatus(null);
      return;
    }
    
    // Validate format first
    const subdomainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
    if (!subdomainPattern.test(subdomain.toLowerCase())) {
      setSubdomainStatus(null);
      return;
    }
    
    setSubdomainStatus('checking');
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/onboarding/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`,
        { method: 'GET' }
      );
      
      const data = await response.json();
      
      if (data.available) {
        setSubdomainStatus('available');
      } else {
        setSubdomainStatus('taken');
        setErrors(prev => ({
          ...prev,
          subdomain: 'This subdomain is already taken. Please choose another.'
        }));
      }
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainStatus(null); // Don't block on error, let the backend validate
    }
  }, []);

  // Check username availability with debounce
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus(null);
      return;
    }
    
    // Validate format first
    const usernamePattern = /^[a-z0-9_]{3,30}$/;
    if (!usernamePattern.test(username.toLowerCase())) {
      setUsernameStatus(null);
      return;
    }
    
    setUsernameStatus('checking');
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/onboarding/check-username?username=${encodeURIComponent(username)}`,
        { method: 'GET' }
      );
      
      const data = await response.json();
      
      if (data.available) {
        setUsernameStatus('available');
      } else {
        setUsernameStatus('taken');
        setErrors(prev => ({
          ...prev,
          username: 'This username is already taken. Please choose another.'
        }));
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameStatus(null); // Don't block on error, let the backend validate
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Debounced subdomain availability check
    if (field === 'subdomain') {
      setSubdomainStatus(null); // Reset status while typing
      
      // Clear previous timeout
      if (subdomainCheckTimeoutRef.current) {
        clearTimeout(subdomainCheckTimeoutRef.current);
      }
      
      // Set new timeout (debounce 500ms)
      subdomainCheckTimeoutRef.current = setTimeout(() => {
        checkSubdomainAvailability(value.trim().toLowerCase());
      }, 500);
    }
    
    // Debounced username availability check
    if (field === 'username') {
      setUsernameStatus(null); // Reset status while typing
      
      // Clear previous timeout
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
      
      // Set new timeout (debounce 500ms)
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(value.trim().toLowerCase());
      }, 500);
    }
  };

  // Handle phone number formatting on blur
  const handlePhoneBlur = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted !== e.target.value) {
      setFormData((prev) => ({
        ...prev,
        phone: formatted,
      }));
    }
  };
  
  // Handle subdomain blur - also check availability
  const handleSubdomainBlur = (e) => {
    const subdomain = e.target.value.trim().toLowerCase();
    if (subdomain && subdomainStatus !== 'checking') {
      checkSubdomainAvailability(subdomain);
    }
  };
  
  // Handle username blur - also check availability
  const handleUsernameBlur = (e) => {
    const username = e.target.value.trim().toLowerCase();
    if (username && usernameStatus !== 'checking') {
      checkUsernameAvailability(username);
    }
  };

  const handleRetry = () => {
    setStep('form');
    setErrorMessage('');
    setProcessingSteps([]);
    setCurrentStepId(null);
    setFailedStepId(null);
    setSubdomainStatus(null);
    setUsernameStatus(null);
  };

  // Send confirmation email to user after successful onboarding
  const sendConfirmationEmail = async (successData) => {
    // Extract domain from dashboard_url (e.g., https://tenant.nasradius.com -> tenant.nasradius.com)
    const domain = successData.dashboard_url 
      ? successData.dashboard_url.replace(/^https?:\/\//i, '').split('/')[0]
      : formData.subdomain ? `${formData.subdomain}.nasradius.com` : '';

    // Get username (use username from successData if available, otherwise use form username, then email)
    const username = successData.username || formData.username.trim().toLowerCase() || formData.email.trim().toLowerCase();

    // Client email template with placeholders replaced
    const clientMessage = `Hello ${formData.name.trim()},

    Welcome to NasRadius — we're excited to have you on board.

    Your site ${domain} is now ready for use.

    Login Credentials
    • Username: ${username}
    • Password: ${successData.password}

    Next Steps
    Log in to your account and navigate to Mikrotiks → Setup Guide to configure your router.

    Please visit the documentation for more information: https://docs.nasradius.com

    If you need any assistance, feel free to reach out—I'm here to help.

    Warm regards,
    Evans Kiplagat
    Founder, NasRadius
    0724 440 202`;

        // Admin email template with placeholders replaced
        const adminMessage = `Hello Admin,

    A new user has onboarded to NasRadius.

    User Details:
    Name: ${formData.name.trim()}
    Email: ${formData.email.trim().toLowerCase()}
    Phone: ${formData.phone}
    Subdomain: ${formData.subdomain}

    Username: ${username}
    Password: ${successData.password}

    Dashboard URL: ${successData.dashboard_url}

    Warm regards,
    Evans Kiplagat
    Founder, NasRadius`;

    try {
      // Send confirmation email to user
      await emailjs.send(
        'service_u5dkan9',       // EmailJS service ID
        'template_f745hyc',      // EmailJS template ID
        {
          to_email: formData.email.trim().toLowerCase(),
          to_name: formData.name.trim(),
          login_username: username,
          login_password: successData.password,
          dashboard_url: successData.dashboard_url,
          domain: domain,
          message: clientMessage,
          subject: "Welcome To Nasradius"
        },
        'ht9UhP00cZq3k6-SH'      // EmailJS user ID
      );

      // Send copy to admin (evanskiplagat@gmail.com)
      await emailjs.send(
        'service_xi01lf4',       // EmailJS service ID
        'template_7rz3aah',      // EmailJS template ID
        {
          to_email: 'evanskiplagat3@gmail.com',
          to_name: 'Admin',
          login_username: username,
          login_password: successData.password,
          dashboard_url: successData.dashboard_url,
          domain: domain,
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone,
          subdomain: formData.subdomain,
          username: username,
          password: successData.password,
          message: adminMessage,
          subject: "New User Onboarded to Nasradius"
        },
        'ht9UhP00cZq3k6-SH'      // EmailJS user ID
      );
    } catch (error) {
      console.error('Failed to send confirmation email:', error);
      // Silently fail - email is sent in background, don't disrupt user experience
    }
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #e8f4fc 0%, #d4f0f5 100%)',
          paddingTop: { xs: '100px', sm: '120px', md: '150px' },
          paddingX: { xs: '12px', sm: '16px', md: '24px' },
          paddingBottom: { xs: '24px', sm: '32px', md: '48px' },
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2 } }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4, lg: 5 }, 
            borderRadius: { xs: '12px', sm: '15px' },
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                fontWeight: 700,
                background: 'linear-gradient(90deg, #19b594, #0b74c1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: 'clamp(1.5rem, 3.4vw, 2.7rem)',
                letterSpacing: '0.5px',
                mb: { xs: 1.5, sm: 2 },
                lineHeight: 1.2,
              }}
            >
              Get Started with NasRadius
            </Typography>
            <Typography 
              variant="body1" 
              sx={{
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                color: '#4A4A4A',
                fontSize: 'clamp(0.9rem, 1vw, 1.15rem)',
                px: { xs: 1, sm: 0 },
                lineHeight: 1.6,
              }}
            >
              Create your account and start managing your network in minutes
            </Typography>
          </Box>

        <ProgressIndicator currentStep={step} />

        {step === 'form' && (
          <OnboardingForm
            formData={formData}
            errors={errors}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            onPhoneBlur={handlePhoneBlur}
            onUsernameBlur={handleUsernameBlur}
            onSubdomainBlur={handleSubdomainBlur}
            onSubmit={handleSubmit}
            subdomainStatus={subdomainStatus}
            usernameStatus={usernameStatus}
          />
        )}

        {step === 'processing' && (
          <Box sx={{ py: { xs: 3, sm: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                  fontWeight: 600,
                  color: '#2C3E50',
                  mb: 1,
                  fontSize: 'clamp(1.05rem, 1.4vw, 1.35rem)',
                }}
              >
                Setting up your account...
              </Typography>
              <Typography 
                variant="body2"
                sx={{
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                  color: '#4A4A4A',
                  fontSize: 'clamp(0.875rem, 0.55vw + 0.78rem, 1rem)',
                  px: { xs: 2, sm: 0 },
                }}
              >
                This may take a few moments. Please don't close this page.
              </Typography>
            </Box>

            {/* Progress Steps Display */}
            <Box sx={{ maxWidth: 500, mx: 'auto', px: { xs: 1, sm: 2 } }}>
              {processingSteps.length > 0 ? (
                <Stepper 
                  orientation="vertical" 
                  sx={{ 
                    '& .MuiStepConnector-line': { 
                      minHeight: 20,
                      borderLeftWidth: 2,
                    },
                  }}
                >
                  {processingSteps.map((stepItem, index) => {
                    const isCompleted = stepItem.status === 'completed';
                    const isFailed = stepItem.status === 'failed';
                    const isInProgress = stepItem.status === 'in_progress';
                    
                    // Determine icon
                    let StepIcon;
                    if (isCompleted) {
                      StepIcon = () => (
                        <CheckCircleIcon sx={{ color: '#19b594', fontSize: 'clamp(1.2rem, 1.8vw, 1.6rem)' }} />
                      );
                    } else if (isFailed) {
                      StepIcon = () => (
                        <ErrorIcon sx={{ color: '#e74c3c', fontSize: 'clamp(1.2rem, 1.8vw, 1.6rem)' }} />
                      );
                    } else if (isInProgress) {
                      StepIcon = () => (
                        <CircularProgress size={20} sx={{ color: '#0b74c1' }} />
                      );
                    } else {
                      StepIcon = () => (
                        <RadioButtonUncheckedIcon sx={{ color: '#bdc3c7', fontSize: 'clamp(1.2rem, 1.8vw, 1.6rem)' }} />
                      );
                    }
                    
                    return (
                      <Step key={stepItem.id} active={true} completed={isCompleted}>
                        <StepLabel
                          StepIconComponent={StepIcon}
                          sx={{
                            '& .MuiStepLabel-label': {
                              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                              fontWeight: isInProgress ? 600 : 400,
                              color: isFailed ? '#e74c3c' : isCompleted ? '#19b594' : isInProgress ? '#0b74c1' : '#7f8c8d',
                              fontSize: 'clamp(0.875rem, 0.5vw + 0.78rem, 1rem)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {stepItem.name}
                            {isInProgress && (
                              <Fade in={true}>
                                <Chip 
                                  label="In Progress" 
                                  size="small"
                                  sx={{ 
                                    height: 20,
                                    fontSize: 'clamp(0.7rem, 0.3vw + 0.64rem, 0.8rem)',
                                    bgcolor: 'rgba(11, 116, 193, 0.1)',
                                    color: '#0b74c1',
                                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                                  }} 
                                />
                              </Fade>
                            )}
                          </Box>
                        </StepLabel>
                        {(stepItem.message || stepItem.error) && (
                          <StepContent>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                                color: isFailed ? '#e74c3c' : '#7f8c8d',
                                display: 'block',
                                mt: -0.5,
                              }}
                            >
                              {stepItem.error || stepItem.message}
                            </Typography>
                          </StepContent>
                        )}
                      </Step>
                    );
                  })}
                </Stepper>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress 
                    size={40} 
                    sx={{ color: '#0b74c1' }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 2,
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                      color: '#4A4A4A',
                    }}
                  >
                    Connecting to server...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {step === 'error' && (
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            {/* Show failed steps if available */}
            {processingSteps.length > 0 && (
              <Box sx={{ maxWidth: 500, mx: 'auto', mb: 3, px: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2,
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                    color: '#2C3E50',
                    fontWeight: 600,
                  }}
                >
                  Setup Progress:
                </Typography>
                {processingSteps.map((stepItem) => {
                  const isCompleted = stepItem.status === 'completed';
                  const isFailed = stepItem.status === 'failed';
                  
                  return (
                    <Box 
                      key={stepItem.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        mb: 1,
                        py: 0.5,
                      }}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon sx={{ color: '#19b594', fontSize: 'clamp(1rem, 1.4vw, 1.35rem)' }} />
                      ) : isFailed ? (
                        <ErrorIcon sx={{ color: '#e74c3c', fontSize: 'clamp(1rem, 1.4vw, 1.35rem)' }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ color: '#bdc3c7', fontSize: 'clamp(1rem, 1.4vw, 1.35rem)' }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                          color: isFailed ? '#e74c3c' : isCompleted ? '#19b594' : '#7f8c8d',
                          fontWeight: isFailed ? 600 : 400,
                        }}
                      >
                        {stepItem.name}
                        {isFailed && stepItem.error && (
                          <Typography 
                            component="span" 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: '#e74c3c',
                              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                            }}
                          >
                            Error: {stepItem.error}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                borderRadius: '8px',
                fontSize: 'clamp(0.875rem, 0.8vw, 1rem)',
              }}
            >
              {errorMessage}
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
              <OnboardingForm
                formData={formData}
                errors={errors}
                isLoading={false}
                onInputChange={handleInputChange}
                onPhoneBlur={handlePhoneBlur}
                onUsernameBlur={handleUsernameBlur}
                onSubmit={handleSubmit}
                showRetry={true}
                onRetry={handleRetry}
                usernameStatus={usernameStatus}
              />
            </Box>
          </Box>
        )}

        {step === 'success' && successData && (
          <OnboardingSuccess successData={successData} />
        )}
      </Paper>
        </Container>
      </Box>
    </>
  );
};

export default OnboardingPage;
